// Teacher/TA form to customize a class: name, description, emoji + colour,
// join policy, member limit, cautioned @slug rename. Mirrors iOS EditClassSheet.
// Modal chrome follows projects/source-ref-modal.tsx.
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { updateClan } from "@/api/clans";
import type { ClanRead, ClanUpdatePayload } from "@/api/community-types";
import { Alert, Button, Input, cx } from "@/components/ui";
import { CLASS_EMOJIS, CLASS_PALETTE, ClassIcon, classSwatch } from "./class-theme";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

export function EditClassModal({
  clan,
  onClose,
  onSaved,
}: {
  clan: ClanRead;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(clan.name);
  const [description, setDescription] = useState(clan.description ?? "");
  const [emoji, setEmoji] = useState(clan.icon_emoji ?? "");
  const [color, setColor] = useState(clan.color ?? "");
  const [inviteOnly, setInviteOnly] = useState((clan.join_policy ?? "open") === "invite");
  const [memberLimit, setMemberLimit] = useState(clan.member_limit);
  const [slug, setSlug] = useState(clan.slug);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: () => {
      const payload: ClanUpdatePayload = {
        name: name.trim(),
        description: description.trim(),
        member_limit: memberLimit,
        icon_emoji: emoji || null,
        color: color || null,
        join_policy: inviteOnly ? "invite" : "open",
      };
      if (slug.trim() !== clan.slug) payload.slug = slug.trim();
      return updateClan(clan.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clan-feed", clan.id] });
      qc.invalidateQueries({ queryKey: ["clans"] });
      onSaved();
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't save.")),
  });

  const canSave = name.trim().length > 0 && SLUG_RE.test(slug.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-bold">{t("Edit class")}</h2>
          <button type="button" onClick={onClose} className="text-ink-faint hover:text-ink">
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {err && <Alert>{err}</Alert>}

          <div className="flex justify-center">
            <ClassIcon emoji={emoji} color={color} size={72} />
          </div>

          <Input label={t("Name")} value={name} onChange={(e) => setName(e.target.value)} />

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Description")}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>

          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Icon")}</span>
            <div className="flex flex-wrap gap-1.5">
              {CLASS_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEmoji(e)}
                  className={cx(
                    "flex h-10 w-10 items-center justify-center rounded-lg text-xl transition",
                    emoji === e ? "bg-brand-100 ring-2 ring-brand-500" : "bg-black/5 hover:bg-black/10",
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Colour")}</span>
            <div className="flex flex-wrap gap-2">
              {CLASS_PALETTE.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setColor(key)}
                  className={cx(
                    "h-7 w-7 rounded-full transition",
                    classSwatch(key),
                    color === key ? "ring-2 ring-offset-2 ring-ink" : "",
                  )}
                  title={key}
                />
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between">
            <span className="text-sm font-medium text-ink-soft">{t("Invite-only")}</span>
            <input
              type="checkbox"
              checked={inviteOnly}
              onChange={(e) => setInviteOnly(e.target.checked)}
              className="h-4 w-4 rounded border-black/20 text-brand-600"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">
              {t("Member limit")}: {memberLimit}
            </span>
            <input
              type="range"
              min={2}
              max={200}
              value={memberLimit}
              onChange={(e) => setMemberLimit(Number(e.target.value))}
              className="w-full"
            />
          </label>

          <div>
            <Input
              label={t("@slug")}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
            />
            <p className="mt-1 text-xs text-ink-faint">
              {t("Changing the @slug breaks existing links. Lowercase letters, digits, hyphens.")}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-black/10 p-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button size="sm" disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
            {t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
